import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import phoenicianLogo from '../../assets/PhoenicianLogo.png';
import resortPool from '../../assets/newpic.jpg';
import { authApi } from '@/services/api';
import { Alert, AlertDescription } from "@/components/UI/alert";
import { Button } from "@/components/UI/button";
import { Loader2 } from 'lucide-react';

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [formData, setFormData] = useState({
        username: '',
        password: ''
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);

    // Check for session expiry or other redirects
    useEffect(() => {
        if (location.search.includes('session=expired')) {
            localStorage.clear();
            setError('Your session has expired. Please login again.');
        }
        
        if (location.search.includes('unauthorized')) {
            setError('You do not have permission to access that page.');
        }
    }, [location]);

    // Store authentication data
    //Update the setAuthData function in your existing Login.jsx
    const setAuthData = async (token, user) => {
        try {
            // Get avatar URL first
            const avatarResponse = await loamsApi.getUserAvatar(user.username);
            const avatarUrl = avatarResponse.data?.avatarUrl;
    
            const finalToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
            localStorage.setItem('token', finalToken);
            localStorage.setItem('user', JSON.stringify({
                ...user,
                displayName: user.username?.split('@')[0] || 'User',
                avatarUrl: avatarUrl // Store avatar URL
            }));
            localStorage.setItem('isAuthenticated', 'true');
            
            const role = user.role.toLowerCase();
            console.log('Setting auth data for role:', role);
            
            localStorage.setItem('hasAccountsAccess', 
                ['super_admin', 'admin', 'accounts', 'loams_only'].includes(role) ? 'true' : 'false'
            );
            localStorage.setItem('hasLoamsAccess', 
                ['super_admin', 'admin', 'accounts', 'engineer', 'loams_only'].includes(role) ? 'true' : 'false'
            );
            localStorage.setItem('hasBoqAccess', 
                ['super_admin', 'admin', 'boq_admin'].includes(role) ? 'true' : 'false'
            );
            localStorage.setItem('isEngineer', role === 'engineer' ? 'true' : 'false');
            localStorage.setItem('isLoamsOnly', role === 'loams_only' ? 'true' : 'false');
            localStorage.setItem('isSuperAdmin', role === 'super_admin' ? 'true' : 'false');
            localStorage.setItem('canAccessOtherInvoices', 
                ['super_admin', 'admin', 'accounts'].includes(role) ? 'true' : 'false'
            );
            
            if (rememberMe) {
                localStorage.setItem('rememberMe', 'true');
            }
            
            return true;
        } catch (e) {
            console.error('[AUTH] Error storing auth data:', e);
            return false;
        }
    };

// Update the handleSubmit function
// Update the handleSubmit function in Login.jsx
const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
        console.log('Submitting login form for user:', formData.username);
        
        const response = await authApi.login({
            username: formData.username.trim(),
            password: formData.password
        });

        console.log('Login successful, redirecting based on role:', response.data.user.role);

        const { token, user } = response.data;
        
        if (setAuthData(token, user)) {
            const role = user.role.toLowerCase();

            // All users first land on accounts dashboard
            switch (role) {
                case 'super_admin':
                case 'admin':
                case 'accounts':
                case 'loams_viewer':
                case 'loams_only':
                    navigate('/accounts');
                    break;
                case 'engineer':
                    navigate('/engineer');
                    break;
                case 'boq_admin':
                    navigate('/boq-dashboard');
                    break;
                default:
                    console.error('Unknown role:', role);
                    setError('Invalid user role configuration');
                    localStorage.clear();
            }
        } else {
            throw new Error('Failed to store authentication data');
        }

    } catch (err) {
        console.error('Login error:', err);
        localStorage.clear();

        let errorMessage = 'Login failed. ';
        if (err.response) {
            console.log('Error response:', err.response.data);
            
            switch (err.response.status) {
                case 401:
                    errorMessage = 'Invalid username or password. Please check your credentials and try again.';
                    break;
                case 403:
                    errorMessage = 'Account is locked or inactive. Please contact support.';
                    break;
                case 404:
                    errorMessage = 'Login service not found. Please try again later.';
                    break;
                case 429:
                    errorMessage = 'Too many login attempts. Please wait a few minutes and try again.';
                    break;
                case 500:
                    errorMessage = 'Server error. Please try again later.';
                    break;
                default:
                    errorMessage = `Login failed: ${err.response.data?.message || 'Unknown error'}`;
            }
        } else if (err.request) {
            errorMessage = 'Unable to reach the server. Please check your connection and try again.';
        } else {
            errorMessage = 'An unexpected error occurred. Please try again.';
        }
        
        setError(errorMessage);
    } finally {
        setIsLoading(false);
    }
};

    return (
        <div className="min-h-screen bg-gray-50 p-6 md:p-8 lg:p-12 flex justify-center items-center">
            <div className="w-full max-w-[1000px] bg-white rounded-xl shadow-lg overflow-hidden flex">
                {/* Left Panel with Background Image */}
                <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                    {/* Background image */}
                    <div 
                        className="absolute inset-0 bg-cover bg-center"
                        style={{
                            backgroundImage: `url(${resortPool})`,
                        }}
                    ></div>
                    
                    {/* Content overlay */}
                    <div className="relative z-20 p-8 flex flex-col justify-between w-full">
                        <div className="space-y-6">
                            <h1 className="text-5xl font-bold text-white tracking-tight leading-tight font-serif drop-shadow-lg shadow-black">
                                 
                            </h1>
                            <p className="text-xl text-white font-medium leading-relaxed drop-shadow-lg shadow-black">
                                
                            </p>
                        </div>
                        
                        <div className="text-sm text-white font-medium drop-shadow-lg shadow-black">
                            © {new Date().getFullYear()} Phoenician Technical Services LLC
                        </div>
                    </div>
                </div>

                {/* Right Panel */}
                <div className="w-full lg:w-1/2 p-8">
                    {/* Logo and Title */}
                    <div className="text-center mb-8">
                        <img
                            src={phoenicianLogo}
                            alt="Phoenician"
                            className="h-14 mx-auto mb-4"
                        />
                        <h2 className="text-xl font-bold text-[#1E2875] mb-1">
                            Phoenician Technical Services LLC
                        </h2>
                        <p className="text-sm text-gray-600">
                            Tomorrow's Landscaping, Today
                        </p>
                    </div>

                    {/* Login Form */}
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {error && (
                            <Alert variant="destructive" className="bg-red-50 border-red-200">
                                <AlertDescription className="text-red-600">
                                    {error}
                                </AlertDescription>
                            </Alert>
                        )}

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    User ID
                                </label>
                                <input
                                    type="text"
                                    value={formData.username}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        username: e.target.value
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg
                                             focus:outline-none focus:ring-2 focus:ring-[#1E2875] 
                                             focus:border-transparent"
                                    placeholder="Enter your User ID"
                                    disabled={isLoading}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Password
                                </label>
                                <input
                                    type="password"
                                    value={formData.password}
                                    onChange={(e) => setFormData({
                                        ...formData,
                                        password: e.target.value
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg
                                             focus:outline-none focus:ring-2 focus:ring-[#1E2875] 
                                             focus:border-transparent"
                                    placeholder="Enter your password"
                                    disabled={isLoading}
                                    required
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="remember-me"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    className="h-4 w-4 text-[#1E2875] focus:ring-[#1E2875] 
                                             border-gray-300 rounded cursor-pointer"
                                />
                                <label htmlFor="remember-me" className="ml-2 text-sm text-gray-600 cursor-pointer">
                                    Remember me
                                </label>
                            </div>
                            <button 
                                type="button" 
                                onClick={() => navigate('/forgot-password')}
                                className="text-sm text-[#1E2875] hover:underline focus:outline-none"
                            >
                                Forgot Password?
                            </button>
                        </div>

                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-[#1E2875] text-white py-2.5 px-4 rounded-lg
                                     hover:bg-[#161d5b] transition-colors duration-200 
                                     focus:outline-none focus:ring-2 focus:ring-offset-2 
                                     focus:ring-[#1E2875] disabled:opacity-50"
                        >
                            {isLoading ? (
                                <span className="flex items-center justify-center">
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Signing in...
                                </span>
                            ) : (
                                'Sign In to Dashboard'
                            )}
                        </Button>

                        <p className="text-center text-sm text-gray-500">
                            Protected by industry standard encryption
                        </p>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Login;